import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Flag,
  Loader2,
  RefreshCw,
  Send,
  FileText,
  Check,
  X,
  AlertCircle,
} from "lucide-react";
import { ADMIN_CLIENTS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_STATUS_LABEL = {
  uploaded: "Uploaded",
  reviewed: "Reviewed",
  needs_resubmission: "Needs resubmission",
};

export const ClientsTab = ({ pw }) => {
  const [clients, setClients] = useState(null);
  const [opened, setOpened] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const headers = { "X-Admin-Password": pw };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { headers });
      setClients(data.clients);
    } catch {
      toast.error("Couldn't load clients.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runBackfill = async () => {
    if (!window.confirm("This will create portal accounts for all paid package buyers without one and email each of them a magic link.\n\nGive your 7 existing clients a heads-up first — they're about to get an email from us. Proceed?")) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/admin/portal/backfill`, {}, { headers });
      toast.success(`Created ${data.created} new accounts · ${data.magic_link_events_fired} magic-link emails fired`);
      load();
    } catch {
      toast.error("Backfill failed.");
    } finally {
      setBackfilling(false);
    }
  };

  if (opened) {
    return <ClientDetail clientId={opened} pw={pw} onBack={() => { setOpened(null); load(); }} />;
  }

  return (
    <div data-testid={ADMIN_CLIENTS.table}>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-extrabold text-[#0D2F4D]">Clients</h2>
          <p className="text-sm text-[#6F8FA8]">{clients?.length ?? "…"} active portal {clients?.length === 1 ? "account" : "accounts"}</p>
        </div>
        <button
          type="button"
          data-testid={ADMIN_CLIENTS.backfillBtn}
          onClick={runBackfill}
          disabled={backfilling}
          className="inline-flex items-center gap-2 bg-[#0D2F4D] text-white text-sm font-bold rounded-full px-4 py-2 hover:bg-[#1a4063] disabled:opacity-60"
        >
          {backfilling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Onboard all paid clients
        </button>
      </div>

      {clients === null ? (
        <div className="flex items-center justify-center py-12"><Loader2 size={28} className="animate-spin text-[#0D2F4D]" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-10 text-center">
          <AlertCircle size={32} className="mx-auto text-[#6F8FA8]" />
          <p className="mt-3 font-display font-bold text-[#0D2F4D]">No client portal accounts yet.</p>
          <p className="text-sm text-[#6F8FA8] mt-1">Once a package is purchased a portal account is auto-created. Or click "Onboard all paid clients" to backfill.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FB] text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Package</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Docs</th>
                <th className="text-left px-4 py-3">Contract</th>
                <th className="text-left px-4 py-3">Last activity</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={ADMIN_CLIENTS.row(c.id)} onClick={() => setOpened(c.id)} className="border-t border-[#E4E7EA] hover:bg-[#FAFBFC] cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.flagged && <Flag size={12} className="text-[#A02E2A] fill-[#A02E2A]" />}
                      <div>
                        <p className="font-bold text-[#0D2F4D]">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-[#6F8FA8]">{c.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{c.package_title}</td>
                  <td className="px-4 py-3 text-xs">
                    <p className="text-[#0D2F4D]">{c.current_stage}</p>
                    <p className="text-[10px] text-[#6F8FA8]">{c.checklist_progress.done}/{c.checklist_progress.total}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0D2F4D]">{c.docs_count}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.agreement_signed ? (
                      <span className="inline-flex items-center gap-1 text-[#0D2F4D] font-bold"><Check size={12} className="text-[#7c8a1a]" /> Signed</span>
                    ) : (
                      <span className="text-[#A02E2A] font-bold">Not signed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6F8FA8]">{c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      data-testid={ADMIN_CLIENTS.open(c.id)}
                      onClick={() => setOpened(c.id)}
                      className="text-xs font-bold text-[#0D2F4D] hover:underline"
                    >
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ClientDetail = ({ clientId, pw, onBack }) => {
  const headers = { "X-Admin-Password": pw };
  const [data, setData] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      const { data: payload } = await axios.get(`${API}/admin/clients/${clientId}`, { headers });
      setData(payload);
      setNotes(payload.client.notes || "");
    } catch {
      toast.error("Couldn't load client.");
    }
  };
  useEffect(() => { load(); }, [clientId]);

  if (!data) return <div className="flex items-center justify-center py-12"><Loader2 size={28} className="animate-spin text-[#0D2F4D]" /></div>;

  const { client, categories, documents, intake, questionnaire } = data;

  const toggleStep = async (step) => {
    try {
      await axios.post(`${API}/admin/clients/${clientId}/checklist`, { step_id: step.id, done: !step.done }, { headers });
      load();
    } catch { toast.error("Couldn't update step."); }
  };
  const reviewDoc = async (doc, status) => {
    const note = status === "needs_resubmission" ? (window.prompt("Note for the client (optional):") || "") : null;
    try {
      await axios.post(`${API}/admin/clients/${clientId}/documents/${doc.id}/review`, { status, note }, { headers });
      toast.success(`${doc.filename} → ${DOC_STATUS_LABEL[status]}`);
      load();
    } catch { toast.error("Couldn't update doc status."); }
  };
  const saveNotes = async () => {
    setSavingNotes(true);
    try { await axios.post(`${API}/admin/clients/${clientId}/notes`, { notes }, { headers }); toast.success("Notes saved"); }
    catch { toast.error("Couldn't save notes."); }
    finally { setSavingNotes(false); }
  };
  const toggleFlag = async () => {
    try { await axios.post(`${API}/admin/clients/${clientId}/flag`, { flagged: !client.flagged }, { headers }); load(); }
    catch { toast.error("Couldn't toggle flag."); }
  };
  const resendLink = async () => {
    if (!window.confirm(`Resend a fresh portal magic-link email to ${client.email}?`)) return;
    try { await axios.post(`${API}/admin/clients/${clientId}/resend-login`, {}, { headers }); toast.success("Magic link resent"); }
    catch { toast.error("Couldn't resend."); }
  };

  return (
    <div>
      <button onClick={onBack} data-testid={ADMIN_CLIENTS.backToList} className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> All clients
      </button>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <div>
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6 mb-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-extrabold text-[#0D2F4D]">{client.first_name} {client.last_name}</h2>
                <p className="text-sm text-[#6F8FA8]">{client.email} · {client.package_title}</p>
                <p className="text-xs text-[#6F8FA8] mt-1">Purchased {new Date(client.purchased_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button data-testid={ADMIN_CLIENTS.flagToggle} onClick={toggleFlag} className={`text-xs font-bold inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${client.flagged ? "bg-[#A02E2A] text-white" : "bg-[#ECF0F4] text-[#0D2F4D] hover:bg-[#D6DCE3]"}`}>
                  <Flag size={11} /> {client.flagged ? "Flagged" : "Flag"}
                </button>
                <button data-testid={ADMIN_CLIENTS.resendLink} onClick={resendLink} className="text-xs font-bold inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-[#0D2F4D] text-white hover:bg-[#1a4063]">
                  <Send size={11} /> Resend portal link
                </button>
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6 mb-5">
            <h3 className="font-display text-lg font-extrabold text-[#0D2F4D] mb-4">Move checklist</h3>
            <ol className="space-y-2">
              {client.checklist?.map((step) => (
                <li key={step.id} className="flex items-center gap-3 rounded-xl border border-[#E4E7EA] px-3 py-2">
                  <button
                    type="button"
                    data-testid={ADMIN_CLIENTS.checklistStep(step.id)}
                    onClick={() => toggleStep(step)}
                    className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 ${step.done ? "bg-[#C6D21F] border-[#C6D21F]" : "border-[#D6DCE3] hover:border-[#0D2F4D]"}`}
                  >
                    {step.done && <Check size={11} strokeWidth={3} className="text-[#0D2F4D]" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${step.done ? "text-[#0D2F4D]" : "text-[#6F8FA8]"}`}>{step.label}</p>
                  </div>
                  {step.done_at && <p className="text-[10px] text-[#6F8FA8]">{new Date(step.done_at).toLocaleDateString()}</p>}
                </li>
              ))}
            </ol>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6 mb-5">
            <h3 className="font-display text-lg font-extrabold text-[#0D2F4D] mb-4">Documents</h3>
            {categories.map((cat) => {
              const docs = documents.filter((d) => d.category === cat.id);
              return (
                <div key={cat.id} className="mb-4 last:mb-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1">{cat.label}</p>
                  {docs.length === 0 ? (
                    <p className="text-xs text-[#A0AEBA] italic">— none uploaded</p>
                  ) : (
                    <div className="space-y-1.5">
                      {docs.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#E4E7EA] px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={13} className="shrink-0 text-[#6F8FA8]" />
                            <a
                              data-testid={ADMIN_CLIENTS.docDownload(doc.id)}
                              href={`${API}/admin/clients/${clientId}/documents/${doc.id}/file`}
                              onClick={(e) => {
                                e.preventDefault();
                                axios.get(`${API}/admin/clients/${clientId}/documents/${doc.id}/file`, { headers, responseType: "blob" })
                                  .then((r) => {
                                    const url = URL.createObjectURL(r.data);
                                    const a = document.createElement("a");
                                    a.href = url; a.download = doc.filename; a.click();
                                    URL.revokeObjectURL(url);
                                  });
                              }}
                              className="text-xs font-bold text-[#0D2F4D] hover:underline truncate inline-flex items-center gap-1"
                            >
                              <Download size={11} /> {doc.filename}
                            </a>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${doc.status === "needs_resubmission" ? "bg-[#FCE7E6] text-[#A02E2A]" : doc.status === "reviewed" ? "bg-[#C6D21F] text-[#0D2F4D]" : "bg-[#ECF0F4] text-[#0D2F4D]"}`}>
                              {DOC_STATUS_LABEL[doc.status] || doc.status}
                            </span>
                            <select
                              data-testid={ADMIN_CLIENTS.docReview(doc.id)}
                              value={doc.status}
                              onChange={(e) => reviewDoc(doc, e.target.value)}
                              className="text-[10px] rounded-md border border-[#D6DCE3] bg-white px-1 py-1 font-bold text-[#0D2F4D] focus:outline-none"
                            >
                              <option value="uploaded">uploaded</option>
                              <option value="reviewed">reviewed</option>
                              <option value="needs_resubmission">needs resubmit</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside>
          {/* Internal notes */}
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6 mb-5">
            <h3 className="font-display text-base font-extrabold text-[#0D2F4D] mb-2">Internal notes</h3>
            <p className="text-[11px] text-[#6F8FA8] mb-2">Not visible to the client.</p>
            <textarea
              data-testid={ADMIN_CLIENTS.notes}
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full text-sm rounded-xl border border-[#E4E7EA] p-3 focus:outline-none focus:border-[#0D2F4D]"
            />
            <button data-testid={ADMIN_CLIENTS.notesSave} onClick={saveNotes} disabled={savingNotes} className="mt-2 text-xs font-bold rounded-full px-4 py-2 bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-60 inline-flex items-center gap-1.5">
              {savingNotes ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save notes
            </button>
          </div>

          {/* Agreement */}
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6 mb-5">
            <h3 className="font-display text-base font-extrabold text-[#0D2F4D] mb-2">Service agreement</h3>
            {client.agreement?.signed ? (
              <div>
                <p className="text-sm text-[#0D2F4D] font-bold">Signed</p>
                <p className="text-xs text-[#6F8FA8] mt-1">{client.agreement.signed_name}</p>
                <p className="text-[11px] text-[#6F8FA8]">{new Date(client.agreement.signed_at).toLocaleString()}</p>
                <p className="text-[10px] text-[#6F8FA8]">IP {client.agreement.signed_ip || "—"}</p>
              </div>
            ) : (
              <p className="text-sm text-[#A02E2A] font-bold">Not signed yet</p>
            )}
          </div>

          {/* Intake / questionnaire snapshot */}
          {intake && (
            <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6">
              <h3 className="font-display text-base font-extrabold text-[#0D2F4D] mb-2">From intake form</h3>
              <dl className="text-xs space-y-1">
                {intake.move_timeline && <Row label="Timeline" value={intake.move_timeline} />}
                {intake.melbourne_area && <Row label="Target city" value={intake.melbourne_area} />}
                {intake.who_is_moving && <Row label="Who's moving" value={intake.who_is_moving} />}
                {intake.employment_situation && <Row label="Employment" value={intake.employment_situation} />}
                {intake.accommodation_budget && <Row label="Budget" value={intake.accommodation_budget} />}
              </dl>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex gap-2">
    <dt className="text-[#6F8FA8] min-w-[90px]">{label}:</dt>
    <dd className="text-[#0D2F4D] font-bold">{value}</dd>
  </div>
);
